import { useState, type CSSProperties } from 'react';
import { cn } from './util';
import { twemojiUrl } from './twemoji';

export interface EmojiProps {
  /** The emoji character / sequence, e.g. "🥙". */
  emoji: string;
  /** Accessible label. Defaults to the emoji itself; pass `""` for purely decorative emoji. */
  label?: string;
  /** CSS size (number = px). Defaults to `1em` so it scales with the surrounding text. */
  size?: number | string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Renders an emoji as a bundled Twemoji SVG for a consistent look on every OS. Falls back to the
 * native emoji glyph if the SVG is missing.
 */
export function Emoji({ emoji, label, size = '1em', className, style }: EmojiProps) {
  const src = twemojiUrl(emoji);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const dim = typeof size === 'number' ? `${size}px` : size;
  const decorative = label === '';
  const alt = label ?? emoji;

  if (failedSrc === src) {
    return (
      <span
        role={decorative ? undefined : 'img'}
        aria-label={decorative ? undefined : alt}
        aria-hidden={decorative || undefined}
        className={cn('inline-block text-center leading-none', className)}
        style={{ fontSize: dim, width: dim, height: dim, verticalAlign: '-0.12em', ...style }}
      >
        {emoji}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={decorative ? '' : alt}
      aria-hidden={decorative || undefined}
      draggable={false}
      decoding="async"
      onError={() => setFailedSrc(src)}
      className={cn('inline-block shrink-0 select-none', className)}
      style={{ width: dim, height: dim, verticalAlign: '-0.12em', ...style }}
    />
  );
}
